/**
 * A prévia dos anexos antes de enviar — o passo que o WhatsApp Web põe entre escolher (ou soltar)
 * um arquivo e mandá-lo (quarta passada sobre o protótipo v2, 29/09/2026).
 *
 * Até a 8.4 o anexo saía **no ato**: escolheu, subiu. Era o caminho mais curto, e era também o
 * que ninguém conhece — no WhatsApp a pessoa vê o que vai mandar, escreve a legenda olhando para
 * a foto e só então envia. A tela passa a fazer o mesmo: o arquivo escolhido pelo clipe, ou solto
 * sobre a conversa, entra aqui; a legenda é digitada por arquivo; e "Enviar" manda um a um, na
 * ordem em que entraram.
 *
 * **O componente não sobe nada.** Ele desenha os anexos que recebeu e devolve as decisões (trocar
 * o selecionado, mudar a legenda, remover, adicionar mais, enviar, fechar); quem tem a mutação e
 * a conversa é o `Chat`. A separação é a mesma de `MidiaDoBalao`: o desenho de um lado, o
 * serviço do outro.
 *
 * **A recusa local vive no anexo.** O teto de tamanho é conferido ao entrar (antes de subir, pela
 * razão de `TAMANHO_MAXIMO_DA_MIDIA`), e o arquivo grande demais **fica na prévia, marcado**: a
 * pessoa vê qual é e o tira; "Enviar" espera até não sobrar recusa nenhuma. Sumir com ele em
 * silêncio faria a pessoa mandar quatro dos cinco sem saber qual ficou.
 *
 * **A prévia de imagem e vídeo é o próprio arquivo** (`URL.createObjectURL`, criado e revogado por
 * quem mantém a lista); documento e áudio mostram o ícone, o nome e o tamanho — o CRM não abre o
 * arquivo para descobrir o que ele é (8.4).
 */

import type { TipoDeMidia } from './api'
import {
  ICONE_DE_AUDIO,
  ICONE_DE_DOCUMENTO,
  TAMANHO_MAXIMO_DA_MIDIA,
  arquivoGrandeDemais,
  formatarTamanho,
  tipoDoArquivo,
} from './midia'

/** Um arquivo esperando na prévia, com a legenda e a recusa local dele. */
export interface AnexoPendente {
  id: string
  arquivo: File
  tipo: TipoDeMidia
  legenda: string
  /** A recusa conferida ao entrar (hoje só o teto de tamanho) — `null` quando ele pode subir. */
  recusa: string | null
  /** A URL de objeto da prévia (imagem e vídeo); `null` para o que não tem prévia. */
  url: string | null
}

export const TITULO_DA_PREVIA = 'Enviar'
export const LEGENDA_DO_ANEXO = 'Adicione uma legenda'
export const DESCARTAR_ANEXOS = 'Descartar os anexos'
export const ADICIONAR_MAIS_ARQUIVOS = 'Adicionar mais arquivos'
export const ENVIAR_ANEXOS = 'Enviar anexos'
export const ENVIANDO_ANEXOS = 'Enviando…'
export const SOLTE_AQUI = 'Solte os arquivos aqui'
export const removerAnexo = (nome: string): string => `Remover ${nome}`
export const selecionarAnexo = (nome: string): string => `Ver ${nome}`

/** O título da prévia: quantos arquivos vão sair. */
export function tituloDaPrevia(quantidade: number): string {
  return quantidade === 1 ? `${TITULO_DA_PREVIA} 1 arquivo` : `${TITULO_DA_PREVIA} ${String(quantidade)} arquivos`
}

let sequencia = 0

/**
 * Monta o anexo pendente de um `File`: o tipo pelo mime, a recusa do teto e a URL de prévia quando
 * o tipo tem prévia. A legenda inicial é a que o chamador passar (o rascunho do compositor, para
 * o primeiro arquivo — é o comportamento do próprio WhatsApp: o que estava escrito acompanha).
 */
export function anexoPendente(arquivo: File, legenda = ''): AnexoPendente {
  sequencia += 1
  const tipo = tipoDoArquivo(arquivo.type)
  const comPrevia = tipo === 'imagem' || tipo === 'video'
  return {
    id: `anexo-${String(sequencia)}`,
    arquivo,
    tipo,
    legenda,
    recusa: arquivo.size > TAMANHO_MAXIMO_DA_MIDIA ? arquivoGrandeDemais() : null,
    url: comPrevia && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(arquivo) : null,
  }
}

/** Libera a URL de objeto de um anexo que saiu da prévia. */
export function liberarAnexo(anexo: AnexoPendente): void {
  if (anexo.url !== null && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(anexo.url)
  }
}

function PreviaDoArquivo({ anexo }: { anexo: AnexoPendente }) {
  const tamanho = formatarTamanho(anexo.arquivo.size)
  if (anexo.tipo === 'imagem' && anexo.url !== null) {
    return <img className="previa-de-anexos__imagem" src={anexo.url} alt={anexo.arquivo.name} />
  }
  if (anexo.tipo === 'video' && anexo.url !== null) {
    return (
      <video
        className="previa-de-anexos__video"
        src={anexo.url}
        controls
        preload="metadata"
        aria-label={anexo.arquivo.name}
      />
    )
  }
  return (
    <div className="previa-de-anexos__arquivo">
      <span className="previa-de-anexos__icone" aria-hidden="true">
        {anexo.tipo === 'audio' ? ICONE_DE_AUDIO : ICONE_DE_DOCUMENTO}
      </span>
      <span className="previa-de-anexos__nome">{anexo.arquivo.name}</span>
      {tamanho === null ? null : <span className="previa-de-anexos__tamanho">{tamanho}</span>}
    </div>
  )
}

export interface PropsDosAnexos {
  anexos: readonly AnexoPendente[]
  selecionadoId: string
  enviando: boolean
  /** Quantos já saíram, enquanto envia — "2 de 5". */
  enviados: number
  aoSelecionar: (id: string) => void
  aoMudarLegenda: (id: string, legenda: string) => void
  aoRemover: (id: string) => void
  aoAdicionarMais: () => void
  aoEnviar: () => void
  aoFechar: () => void
}

export function AnexosParaEnviar({
  anexos,
  selecionadoId,
  enviando,
  enviados,
  aoSelecionar,
  aoMudarLegenda,
  aoRemover,
  aoAdicionarMais,
  aoEnviar,
  aoFechar,
}: PropsDosAnexos) {
  const selecionado = anexos.find((anexo) => anexo.id === selecionadoId) ?? anexos[0]
  if (selecionado === undefined) {
    return null
  }
  const algumaRecusa = anexos.some((anexo) => anexo.recusa !== null)
  const titulo = tituloDaPrevia(anexos.length)

  return (
    <div
      className="previa-de-anexos"
      role="dialog"
      aria-label={titulo}
      onKeyDown={(evento) => {
        if (evento.key === 'Escape' && !enviando) {
          evento.stopPropagation()
          aoFechar()
        }
      }}
    >
      <div className="previa-de-anexos__topo">
        <button
          type="button"
          className="previa-de-anexos__fechar"
          aria-label={DESCARTAR_ANEXOS}
          title={DESCARTAR_ANEXOS}
          disabled={enviando}
          onClick={aoFechar}
        >
          <span aria-hidden="true">×</span>
        </button>
        <h3 className="previa-de-anexos__titulo">{titulo}</h3>
      </div>

      <div className="previa-de-anexos__palco">
        <PreviaDoArquivo anexo={selecionado} />
      </div>

      {selecionado.recusa === null ? null : (
        <p className="previa-de-anexos__recusa" role="alert">
          {selecionado.recusa}
        </p>
      )}

      <label className="previa-de-anexos__legenda">
        <input
          type="text"
          className="previa-de-anexos__legenda-campo"
          placeholder={LEGENDA_DO_ANEXO}
          aria-label={LEGENDA_DO_ANEXO}
          value={selecionado.legenda}
          disabled={enviando}
          onChange={(evento) => {
            aoMudarLegenda(selecionado.id, evento.target.value)
          }}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter' && !enviando && !algumaRecusa) {
              evento.preventDefault()
              aoEnviar()
            }
          }}
        />
      </label>

      <div className="previa-de-anexos__rodape">
        <ul className="previa-de-anexos__fila" aria-label="Arquivos a enviar">
          {anexos.map((anexo) => (
            <li
              key={anexo.id}
              className="previa-de-anexos__item"
              data-selecionado={anexo.id === selecionado.id ? 'sim' : 'nao'}
              data-recusado={anexo.recusa === null ? 'nao' : 'sim'}
            >
              <button
                type="button"
                className="previa-de-anexos__miniatura"
                aria-label={selecionarAnexo(anexo.arquivo.name)}
                aria-pressed={anexo.id === selecionado.id}
                onClick={() => {
                  aoSelecionar(anexo.id)
                }}
              >
                {anexo.tipo === 'imagem' && anexo.url !== null ? (
                  <img src={anexo.url} alt="" />
                ) : (
                  <span aria-hidden="true">
                    {anexo.tipo === 'video' ? '🎬' : anexo.tipo === 'audio' ? ICONE_DE_AUDIO : ICONE_DE_DOCUMENTO}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="previa-de-anexos__remover"
                aria-label={removerAnexo(anexo.arquivo.name)}
                title={removerAnexo(anexo.arquivo.name)}
                disabled={enviando}
                onClick={() => {
                  aoRemover(anexo.id)
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
          <li className="previa-de-anexos__item">
            <button
              type="button"
              className="previa-de-anexos__mais"
              aria-label={ADICIONAR_MAIS_ARQUIVOS}
              title={ADICIONAR_MAIS_ARQUIVOS}
              disabled={enviando}
              onClick={aoAdicionarMais}
            >
              <span aria-hidden="true">＋</span>
            </button>
          </li>
        </ul>
        <button
          type="button"
          className="previa-de-anexos__enviar"
          aria-label={enviando ? ENVIANDO_ANEXOS : ENVIAR_ANEXOS}
          title={enviando ? ENVIANDO_ANEXOS : ENVIAR_ANEXOS}
          disabled={enviando || algumaRecusa}
          onClick={aoEnviar}
        >
          {enviando ? (
            <span className="previa-de-anexos__progresso">
              {String(enviados)}/{String(anexos.length)}
            </span>
          ) : (
            <span aria-hidden="true">➤</span>
          )}
        </button>
      </div>
    </div>
  )
}
